import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Detect if running in Capacitor native app
  const isNativeApp = useMemo(() => {
    return window.Capacitor?.isNativePlatform?.() || false;
  }, []);

  // Detect iOS once
  const isIOS = useMemo(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }, []);

  // Detect standalone mode once
  const isStandalone = useMemo(() => {
    return window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
  }, []);

  useEffect(() => {
    // Don't show in native app or standalone mode
    if (isNativeApp || isStandalone) return;

    // Check if user dismissed before (don't show again for 7 days)
    const dismissed = localStorage.getItem('boat360_install_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDays) {
        return;
      }
    }

    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay (let user see the app first)
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show custom instructions after delay
    if (isIOS) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isIOS, isStandalone, isNativeApp]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Trigger native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('boat360_install_dismissed', Date.now().toString());
  };

  // Don't show if not ready
  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 animate-in slide-in-from-bottom duration-300">
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl shadow-2xl p-4 mx-auto max-w-md">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-white/70 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white text-base">Install Boat 360</h3>
            <p className="text-cyan-100 text-sm mt-0.5">
              Add to your home screen for the best experience
            </p>
            
            {isIOS ? (
              // iOS instructions (can't auto-install)
              <div className="mt-3 bg-white/10 rounded-lg p-2.5 text-xs text-white">
                <p className="font-medium mb-1">To install:</p>
                <ol className="space-y-1 text-cyan-100">
                  <li>1. Tap the <strong>Share</strong> button <span className="inline-block w-4 h-4 align-middle">⬆️</span></li>
                  <li>2. Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></li>
                  <li>3. Tap <strong>Add</strong></li>
                </ol>
              </div>
            ) : (
              // Android/Desktop install button
              <Button
                onClick={handleInstall}
                className="mt-3 bg-white text-cyan-700 hover:bg-cyan-50 font-semibold h-9 px-4"
              >
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
            )}
          </div>
        </div>
        
        <p className="text-[10px] text-cyan-200/70 mt-3 text-center">
          Works offline • No app store needed • Free
        </p>
      </div>
    </div>
  );
};

export default InstallPrompt;
